import { computed, effectScope, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { useOnline } from '@vueuse/core';
import { useSocket } from '@/composition/use-socket';
import { useAuthStore } from '@/composition/use-auth';

export type TConnectivityStatus =
  | 'online'
  | 'reconnecting'
  | 'offline'
  | 'restored';

export interface IConnectivityControllerDeps {
  isConnected: Ref<boolean>;
  isBrowserOnline: Ref<boolean>;
  isEnabled: Ref<boolean>;
}

export interface IConnectivityController {
  status: Ref<TConnectivityStatus>;
  stop: () => void;
}

/** Socket drops shorter than this show "reconnecting" instead of "offline",
 *  absorbing deploy blips, the initial boot window and post-login connects. */
const GRACE_MS = 4000;
/** How long the green "connection restored" confirmation stays visible. */
const RESTORED_MS = 3000;

/**
 * Connectivity state machine. Pure with respect to its deps so it can be
 * unit-tested with plain refs and fake timers.
 *
 * Branch order is mandatory (enabled -> browser-offline -> connected ->
 * grace): `navigator.onLine === false` must override a socket that still
 * *looks* connected, because the OS notices an outage long before socket.io's
 * ping timeout does (and Chromium's offline emulation never severs
 * established WebSockets).
 */
export function createConnectivityController(
  deps: IConnectivityControllerDeps
): IConnectivityController {
  const status = ref<TConnectivityStatus>('online');

  let graceTimer: ReturnType<typeof setTimeout> | null = null;
  let restoredTimer: ReturnType<typeof setTimeout> | null = null;

  function clearTimers() {
    if (graceTimer) {
      clearTimeout(graceTimer);
      graceTimer = null;
    }
    if (restoredTimer) {
      clearTimeout(restoredTimer);
      restoredTimer = null;
    }
  }

  const unwatch = watch(
    [deps.isEnabled, deps.isConnected, deps.isBrowserOnline],
    ([enabled, connected, browserOnline]) => {
      clearTimers();

      if (!enabled) {
        // Idle while logged out: the socket is intentionally disconnected
        // then, and idling prevents a stale "offline" from flashing a
        // spurious "restored" banner right after the next login.
        status.value = 'online';
      } else if (!browserOnline) {
        status.value = 'offline';
      } else if (connected) {
        if (status.value === 'offline') {
          status.value = 'restored';
          restoredTimer = setTimeout(() => {
            status.value = 'online';
          }, RESTORED_MS);
        } else {
          status.value = 'online';
        }
      } else if (status.value !== 'offline') {
        status.value = 'reconnecting';
        graceTimer = setTimeout(() => {
          status.value = 'offline';
        }, GRACE_MS);
      }
      // else: browser online, socket down, already offline -> stay offline.
    },
    { immediate: true }
  );

  function stop() {
    unwatch();
    clearTimers();
  }

  return { status, stop };
}

let controller: IConnectivityController | null = null;

/**
 * App-wide connectivity status singleton. Lazily created on first call inside
 * a detached effect scope so the watcher survives the first calling
 * component's unmount (logout, `noMenus` routes).
 */
export function useConnectivity(): { status: Ref<TConnectivityStatus> } {
  if (!controller) {
    const scope = effectScope(true);
    const created = scope.run(() =>
      createConnectivityController({
        isConnected: useSocket().isConnected,
        isBrowserOnline: useOnline(),
        isEnabled: computed(() => !!useAuthStore().user),
      })
    );
    if (!created) {
      // Unreachable: a freshly created scope is always active.
      throw new Error('Failed to create connectivity controller');
    }
    controller = created;
  }
  return { status: controller.status };
}
